import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import bgImage from '../assets/background_login_panel.png';
import api from '../services/api';

const authContainerStyle = {
  backgroundImage: `url(${bgImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  minHeight: '100vh'
};

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: email, 2: code + new password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await api.post('/auth/forgot-password', { email });
      setCountdown(response.data.expires_in_seconds);
      setStep(2);
      setSuccess('Verification code sent to your email.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = useCallback(async () => {
    setError('');
    setIsLoading(true);

    try {
      const response = await api.post('/auth/forgot-password', { email });
      setCountdown(response.data.expires_in_seconds);
      setSuccess('New verification code sent to your email.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password requirements
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }

    if (!/[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'`~]/.test(newPassword)) {
      setError('Password must contain at least one special character');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/auth/reset-password', {
        email,
        code,
        new_password: newPassword,
      });
      setSuccess('Password successfully changed! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container" style={authContainerStyle}>
      <div className="auth-card">
        <h2>🔐 Resetowanie hasła</h2>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {step === 1 && (
          <form onSubmit={handleEmailSubmit}>
            <p className="auth-description">
              Wpisz swój adres email, a my wyślemy Ci kod weryfikacyjny, który pozwoli Ci ustawić nowe hasło.
            </p>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Wpisz swój email"
                required
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Wysyłanie...' : 'Wyślij kod resetujący'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleResetPassword}>
            <p className="auth-description">
              Wpisz kod weryfikacyjny wysłany na <strong>{email}</strong> oraz nowe hasło.
            </p>

            {countdown > 0 && (
              <div className="countdown-timer">
                ⏱️ Kod wygasa za: <strong>{countdown}s</strong>
              </div>
            )}

            {countdown === 0 && (
              <div className="countdown-expired">
                ⚠️ Kod wygasł.{' '}
                <button
                  type="button"
                  className="btn-link"
                  onClick={handleResendCode}
                  disabled={isLoading}
                >
                  Wyślij ponownie kod
                </button>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="code">Kod weryfikacyjny</label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Wpisz kod weryfikacyjny"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">Nowe hasło</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Wpisz nowe hasło"
                required
              />
              <small className="form-hint">
                Min 8 znaków, 1 wielka litera, 1 znak specjalny
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Potwierdź hasło</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Potwierdź nowe hasło"
                required
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={isLoading || countdown === 0}>
              {isLoading ? 'Resetowanie...' : 'Zresetuj hasło'}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setStep(1);
                setCode('');
                setNewPassword('');
                setConfirmPassword('');
                setError('');
                setSuccess('');
              }}
              style={{ marginTop: '10px' }}
            >
              ← Powrót do Email
            </button>
          </form>
        )}

        <div className="auth-switch">
          Pamiętasz swoje hasło? <Link to="/login">Zaloguj się</Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
